import React from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import Text from '~/components/core/TextComponents/Text';
import TextFieldFormik from '~/components/TextFieldFormik';
import { GenerateReportRequestGeneratedReportExportFormat } from '~/server_shared/openapi/data-contracts';
import { DATA_EXPORT } from '~/Types';

import { ReportTemplatesProvider, useReportTemplates } from '../ReportTemplates/hooks/useReportTemplates';
import type { ReportParameters } from '../shared/ReportParameterFields';
import ReportParameterFields, {
  createParametersValidation,
  stripEmptyParameters,
} from '../shared/ReportParameterFields';
import ReportTemplateSelect from '../shared/ReportTemplateSelect';

import { useSnapshotDates } from './hooks/useSnapshotDates';
import { GENERATED_REPORTS_TEXTS } from './texts';

export type FormatKey = GenerateReportRequestGeneratedReportExportFormat;

const FORMAT_OPTION_KEYS = [
  GenerateReportRequestGeneratedReportExportFormat.Excel,
  GenerateReportRequestGeneratedReportExportFormat.Pdf,
] as const;

const FORMAT_OPTIONS = FORMAT_OPTION_KEYS.map((key) => ({
  text: DATA_EXPORT.EXPORT_FORMAT[key].desc,
  optionValue: key,
}));

interface GenerateReportFormValues {
  name: string;
  report_template_id: number | '';
  parameters: ReportParameters;
  snapshot_date: string;
  export_format: FormatKey;
}

interface GenerateReportDialogProps {
  onClose: () => void;
  onSubmit: (values: {
    name: string;
    report_template_id: number;
    parameters?: ReportParameters | null;
    snapshot_date?: string | null;
    export_format: FormatKey;
  }) => Promise<boolean>;
}

function formatSnapshotDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const GenerateReportDialog: React.FC<GenerateReportDialogProps> = (props) => (
  <ReportTemplatesProvider>
    <GenerateReportDialogContent {...props} />
  </ReportTemplatesProvider>
);

const GenerateReportDialogContent: React.FC<GenerateReportDialogProps> = ({ onClose, onSubmit }) => {
  const { templates } = useReportTemplates();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Report name is required').max(255, 'Name must be 255 characters or less'),
    report_template_id: Yup.number().required('Please select a template').positive('Please select a template'),
    parameters: createParametersValidation(templates),
  });

  const initialValues: GenerateReportFormValues = {
    name: '',
    report_template_id: '',
    parameters: {},
    snapshot_date: '',
    export_format: GenerateReportRequestGeneratedReportExportFormat.Excel,
  };

  const handleFormSubmit = async (values: GenerateReportFormValues) => {
    if (values.report_template_id === '') return;

    const success = await onSubmit({
      name: values.name,
      report_template_id: values.report_template_id,
      parameters: stripEmptyParameters(values.parameters),
      snapshot_date: values.snapshot_date || null,
      export_format: values.export_format,
    });

    if (success) {
      onClose();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      <GenerateReportDialogInner onClose={onClose} />
    </Formik>
  );
};

interface GenerateReportDialogInnerProps {
  onClose: () => void;
}

const GenerateReportDialogInner: React.FC<GenerateReportDialogInnerProps> = ({ onClose }) => {
  const { isSubmitting, handleSubmit, values, setFieldValue, setFieldTouched } =
    useFormikContext<GenerateReportFormValues>();
  const { dates, isLoading: isDatesLoading } = useSnapshotDates();

  return (
    <CardDialog
      isDialog
      title={GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_TITLE}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      preventClose={isSubmitting}
    >
      <div className="mb-20 flex flex-col gap-12">
        <TextFieldFormik
          id="name"
          label={GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_NAME_LABEL}
          placeholder={GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_NAME_PLACEHOLDER}
          fullWidth
          disabled={isSubmitting}
        />
        <ReportTemplateSelect disabled={isSubmitting} />
        <ReportParameterFields disabled={isSubmitting} />
        <SingleSelectField
          id="snapshot_date"
          label={GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_AS_OF_DATE_LABEL}
          value={values.snapshot_date}
          onChange={(val) => setFieldValue('snapshot_date', val)}
          onBlur={() => setFieldTouched('snapshot_date', true)}
          fullWidth
          disabled={isSubmitting || isDatesLoading}
        >
          <MenuItem value="">{GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_AS_OF_DATE_LIVE}</MenuItem>
          {dates.map((dateStr) => (
            <MenuItem key={dateStr} value={dateStr}>
              {formatSnapshotDate(dateStr)}
            </MenuItem>
          ))}
        </SingleSelectField>
        <div>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM} className="mb-4">
            {GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_FORMAT_LABEL}
          </Text>
          <RadioButtonGroupFormik id="export_format" direction="row" options={FORMAT_OPTIONS} showOnly={isSubmitting} />
        </div>
        <ButtonsContainer className="gap-3">
          <CancelButton disabled={isSubmitting} onClick={onClose} />
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting || !values.report_template_id}
            onClick={() => handleSubmit()}
          >
            {GENERATED_REPORTS_TEXTS.GENERATE_DIALOG_SUBMIT}
          </Button>
        </ButtonsContainer>
      </div>
    </CardDialog>
  );
};

export default GenerateReportDialog;
